import { useState, useEffect, useMemo } from 'react';
import { C } from '../../lib/tokens.js';
import { useViewport } from '../../lib/useViewport.js';
import { t } from '../../lib/i18n.jsx';
import { useLookups } from '../../lib/LookupsContext.jsx';
import { RequestFilters, applyFilters, WAITING_FOR_ME_STATUS_VALUES } from './views/FilterBar.jsx';
import { KanbanBoard } from './views/KanbanBoard.jsx';
import { RequestTimeline } from './views/RequestTimeline.jsx';
import { SortSelect } from './views/shared.jsx';
import { PageSpinner } from '../../components/ui/Misc.jsx';

function statusKpis(requests, closedStatusIds, closedTotal, quoteStatusIds) {
  const waiting = quoteStatusIds.size > 0
    ? [{ id: 'quote', label: t('billing.waiting_quote_kpi'), color: '#25A8E0', count: requests.filter(r => quoteStatusIds.has(r.status)).length }]
    : [];
  return [
    { id: 'open',   label: 'Open',   color: '#5CAF01', count: requests.filter(r => !closedStatusIds.has(r.status)).length },
    ...waiting,
    { id: 'closed', label: 'Closed', color: '#696969', count: closedTotal },
  ];
}

function categoryLabel(cat) {
  return cat.name || cat.id;
}

function CategorySelector({ categories, value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = categories.find(c => c.id === value);
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', borderRadius: 4,
          border: `1px solid ${C.mercury}`, background: C.platinum,
          color: C.deepsea, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'Roboto',
        }}>
        <span className="material-icons" style={{ fontSize: 16, color: C.liquid }}>view_kanban</span>
        {current ? categoryLabel(current) : 'Select board'}
        <span className="material-icons" style={{ fontSize: 14, color: C.iron }}>expand_more</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100,
            background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 4,
            boxShadow: '0 6px 16px rgba(34,52,105,0.14)', minWidth: 220, padding: 4,
          }}>
            {categories.map(cat => {
              const active = cat.id === value;
              return (
                <button key={cat.id} type="button"
                  onClick={() => { onChange(cat.id); setOpen(false); }}
                  style={{
                    width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none',
                    background: active ? C.rowActive : 'transparent',
                    color: active ? C.deepsea : C.fg,
                    fontSize: 13, cursor: 'pointer', borderRadius: 3, fontFamily: 'Roboto',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                  {active && <span className="material-icons" style={{ fontSize: 14, color: C.liquid }}>check</span>}
                  {!active && <span style={{ width: 14 }} />}
                  {categoryLabel(cat)}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function RequestsPage({
  requests, loading, isSearchMode, searchResults, searchLoading, filters, setFilters, role, me,
  statusCategories, requestTypes,
  closedStatusIds, closedTotal,
  view, onViewChange, tweaks,
  onOpen, onPeek, onMove,
  hasMore, onLoadMore, loadingMore,
}) {
  const { isMobile } = useViewport();
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [boardSort, setBoardSort] = useState('default');
  const { statuses: allStatuses } = useLookups() ?? {};
  const quoteStatusIds = useMemo(() => new Set((allStatuses ?? []).filter(s => s.is_quote).map(s => s.id)), [allStatuses]);
  const finalClosedStatusIds = useMemo(() => new Set((allStatuses ?? []).filter(s => s.terminal).map(s => s.id)), [allStatuses]);
  const waitingStatusIds = useMemo(() => new Set((allStatuses ?? []).filter(s => WAITING_FOR_ME_STATUS_VALUES.includes(s.value)).map(s => s.id)), [allStatuses]);

  // Kanban's drag-and-drop is native HTML5 DnD (draggable/dataTransfer), which never
  // fires on touch - dragging cards between columns is unusable on a phone. Rather
  // than reimplement drag with touch support, fall back to the timeline on mobile;
  // the persisted `board` preference is left alone so it comes back on desktop.
  // Anything else (e.g. a saved "list" / "cards" from before those views were
  // removed) is shown as the board.
  const effectiveView = view === 'timeline' || (view === 'board' && isMobile) ? 'timeline' : 'board';

  useEffect(() => {
    if (statusCategories.length > 0 && !selectedCatId) {
      const def = statusCategories.find(c => c.is_default) ?? statusCategories[0];
      setSelectedCatId(def.id);
    }
  }, [statusCategories, selectedCatId]);

  const displayRequests = isSearchMode ? searchResults : requests;

  const filtered = useMemo(
    // Mobile only: "assigned to me" there hides permanently closed requests.
    () => applyFilters(displayRequests, filters, role, me.id, isSearchMode, isMobile ? finalClosedStatusIds : null, waitingStatusIds),
    [displayRequests, filters, role, me.id, isSearchMode, isMobile, finalClosedStatusIds, waitingStatusIds],
  );

  const categoryFiltered = useMemo(() => {
    if (!selectedCatId) return filtered;
    const catTypes = requestTypes.filter(t => t.status_category_id === selectedCatId);
    if (catTypes.length === 0) return filtered;
    const catTypeIds = new Set(catTypes.map(t => t.id));
    return filtered.filter(r => catTypeIds.has(r.type));
  }, [filtered, selectedCatId, requestTypes]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '20px 24px 12px', background: C.tin, borderBottom: `1px solid ${C.mercury}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontFamily: 'Roboto', fontWeight: 300, fontSize: 26, color: C.deepsea }}>{t('req.title')}</h1>
            <div style={{ fontSize: 13, color: C.iron, marginTop: 4 }}>
              {isSearchMode
                ? searchLoading
                  ? 'Searching…'
                  : <span>
                      {categoryFiltered.length} result{categoryFiltered.length !== 1 ? 's' : ''} for &ldquo;{filters.q}&rdquo;
                      &ensp;&middot;&ensp;
                      <button type="button"
                        onClick={() => setFilters(f => ({ ...f, q: '' }))}
                        style={{ border: 'none', background: 'none', color: C.liquid, cursor: 'pointer', fontSize: 13, padding: 0, fontFamily: 'Roboto' }}>
                        Clear search
                      </button>
                    </span>
                : role === 'agent'
                  ? t('req.subtitle_agent')
                  : (filters.scope || 'mine') === 'company'
                    ? t('req.subtitle_company')
                    : t('req.subtitle')
              }
            </div>
          </div>
          {statusCategories.length > 1 && (
            <CategorySelector categories={statusCategories} value={selectedCatId} onChange={setSelectedCatId} />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, overflowX: isMobile ? 'auto' : 'visible', flexWrap: isMobile ? 'nowrap' : 'wrap', paddingBottom: isMobile ? 2 : 0 }}>
          {statusKpis(categoryFiltered, closedStatusIds, closedTotal, quoteStatusIds).map(k => (
            <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 999, background: C.platinum, border: `1px solid ${C.mercury}`, flexShrink: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: k.color }} />
              <span style={{ fontSize: 11, color: C.iron, fontWeight: 500 }}>{k.label}</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: C.fg }}>{k.count}</span>
            </div>
          ))}
          {!isMobile && <div style={{ flex: 1 }} />}
          {!isMobile && (
            <div style={{ display: 'inline-flex', padding: 2, background: C.platinum, border: `1px solid ${C.mercury}`, borderRadius: 4 }}>
              {[
                { id: 'board',    icon: 'view_kanban', label: t('req.view.board') },
                { id: 'timeline', icon: 'timeline',    label: t('req.view.timeline') },
              ].map(v => {
                const active = v.id === effectiveView;
                return (
                  <button key={v.id} type="button" onClick={() => onViewChange(v.id)} title={v.label}
                    style={{
                      background: active ? C.tin : 'transparent', color: active ? C.deepsea : C.iron,
                      border: active ? `1px solid ${C.mercury}` : '1px solid transparent',
                      boxShadow: active ? '0 1px 1px rgba(34,52,105,0.06)' : 'none',
                      padding: '4px 10px', borderRadius: 3, fontSize: 12, fontWeight: 500,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'Roboto',
                    }}>
                    <span className="material-icons" style={{ fontSize: 14 }}>{v.icon}</span>
                    {v.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <RequestFilters filters={filters} setFilters={setFilters} role={role} view={effectiveView}
        trailing={effectiveView === 'board'
          ? <SortSelect value={boardSort} label={t('board.sort')} title={t('board.sort.all')} onChange={setBoardSort} />
          : null} />
      {loading && !isSearchMode ? (
        <PageSpinner label="Loading requests…" />
      ) : (
        <>
          {effectiveView === 'board'    && <KanbanBoard     requests={categoryFiltered} onOpen={onOpen} onPeek={onPeek} density={tweaks.density} showPriority={tweaks.showPriority} role={role} onMove={onMove} hasMore={hasMore} onLoadMore={onLoadMore} loadingMore={loadingMore} categoryId={selectedCatId} sort={boardSort} />}
          {effectiveView === 'timeline' && <RequestTimeline  requests={categoryFiltered} onOpen={onOpen} showPriority={tweaks.showPriority} hasMore={hasMore} onLoadMore={onLoadMore} loadingMore={loadingMore} />}
        </>
      )}
    </div>
  );
}
